import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Plus, Edit, Trash2, MapPin, FileText, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRMSettings = ({ user, onLogout }) => {
  const navigate = useNavigate();
  
  const [regions, setRegions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Region dialog
  const [regionDialogOpen, setRegionDialogOpen] = useState(false);
  const [editingRegion, setEditingRegion] = useState(null);
  const [regionForm, setRegionForm] = useState({ name: '', code: '' });
  
  // Program dialog
  const [programDialogOpen, setProgramDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [programForm, setProgramForm] = useState({ name: '', description: '' });
  
  // Category dialog
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '' });

  // Unified Delete-confirmation AlertDialog state (works on mobile, unlike window.confirm)
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    affectedCount: 0,
    onConfirm: null,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [regionsRes, programsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/crm/regions?active_only=false&user_id=${user.user_id}`),
        axios.get(`${API}/crm/programs?active_only=false&user_id=${user.user_id}`),
        axios.get(`${API}/crm/categories?active_only=false&user_id=${user.user_id}`)
      ]);
      setRegions(regionsRes.data);
      setPrograms(programsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης');
    } finally {
      setLoading(false);
    }
  };

  // Region handlers
  const openRegionDialog = (region = null) => {
    if (region) {
      setEditingRegion(region);
      setRegionForm({ name: region.name, code: region.code || '' });
    } else {
      setEditingRegion(null);
      setRegionForm({ name: '', code: '' });
    }
    setRegionDialogOpen(true);
  };

  const handleSaveRegion = async (e) => {
    e.preventDefault();
    if (!regionForm.name.trim()) {
      toast.error('Το όνομα είναι υποχρεωτικό');
      return;
    }
    
    try {
      if (editingRegion) {
        await axios.put(
          `${API}/crm/regions/${editingRegion.region_id}?user_id=${user.user_id}`,
          regionForm
        );
        toast.success('Η περιφέρεια ενημερώθηκε');
      } else {
        await axios.post(`${API}/crm/regions?user_id=${user.user_id}`, regionForm);
        toast.success('Η περιφέρεια δημιουργήθηκε');
      }
      setRegionDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης');
    }
  };

  const handleDeleteRegion = async (regionId) => {
    // 1) Cascade check
    let affectedCount = 0;
    try {
      const usage = await axios.get(`${API}/crm/regions/${regionId}/usage?user_id=${user.user_id}`);
      affectedCount = usage.data?.customers_count || 0;
    } catch (e) { /* non-blocking */ }

    // 2) Open the confirmation dialog (mobile-friendly, not window.confirm)
    setConfirmDialog({
      open: true,
      title: '⚠ Οριστική Διαγραφή Περιφέρειας',
      message: affectedCount > 0
        ? `Αυτή η περιφέρεια χρησιμοποιείται από ${affectedCount} πελάτη/ες. Αν την διαγράψετε, θα μείνουν χωρίς περιφέρεια (κενό πεδίο). Δεν αναιρείται.`
        : 'Θα διαγραφεί οριστικά. Δεν αναιρείται.',
      affectedCount,
      onConfirm: async () => {
        try {
          const resp = await axios.delete(`${API}/crm/regions/${regionId}?user_id=${user.user_id}`);
          const affected = resp.data?.affected_customers || 0;
          if (affected > 0) toast.warning(`Η περιφέρεια διαγράφηκε. ${affected} πελάτες έμειναν χωρίς περιφέρεια.`);
          else toast.success('Η περιφέρεια διαγράφηκε');
          fetchData();
        } catch (error) {
          const status = error?.response?.status;
          if (status === 404) toast.error('Η περιφέρεια δεν βρέθηκε ή δεν σας ανήκει');
          else toast.error('Σφάλμα διαγραφής');
        }
      },
    });
  };

  // Program handlers
  const openProgramDialog = (program = null) => {
    if (program) {
      setEditingProgram(program);
      setProgramForm({ name: program.name, description: program.description || '' });
    } else {
      setEditingProgram(null);
      setProgramForm({ name: '', description: '' });
    }
    setProgramDialogOpen(true);
  };

  const handleSaveProgram = async (e) => {
    e.preventDefault();
    if (!programForm.name.trim()) {
      toast.error('Το όνομα είναι υποχρεωτικό');
      return;
    }
    
    try {
      if (editingProgram) {
        await axios.put(
          `${API}/crm/programs/${editingProgram.program_id}?user_id=${user.user_id}`,
          programForm
        );
        toast.success('Το πρόγραμμα ενημερώθηκε');
      } else {
        await axios.post(`${API}/crm/programs?user_id=${user.user_id}`, programForm);
        toast.success('Το πρόγραμμα δημιουργήθηκε');
      }
      setProgramDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης');
    }
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm('Απενεργοποίηση προγράμματος;')) return;
    
    try {
      await axios.delete(`${API}/crm/programs/${programId}?user_id=${user.user_id}`);
      toast.success('Το πρόγραμμα απενεργοποιήθηκε');
      fetchData();
    } catch (error) {
      toast.error('Σφάλμα');
    }
  };

  // Category handlers
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({ name: category.name });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '' });
    }
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name.trim()) {
      toast.error('Το όνομα είναι υποχρεωτικό');
      return;
    }
    
    try {
      if (editingCategory) {
        await axios.put(
          `${API}/crm/categories/${editingCategory.category_id}?user_id=${user.user_id}`,
          categoryForm
        );
        toast.success('Η κατηγορία ενημερώθηκε');
      } else {
        await axios.post(`${API}/crm/categories?user_id=${user.user_id}`, categoryForm);
        toast.success('Η κατηγορία δημιουργήθηκε');
      }
      setCategoryDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Απενεργοποίηση κατηγορίας;')) return;
    
    try {
      await axios.delete(`${API}/crm/categories/${categoryId}?user_id=${user.user_id}`);
      toast.success('Η κατηγορία απενεργοποιήθηκε');
      fetchData();
    } catch (error) {
      toast.error('Σφάλμα');
    }
  };


  // Owner-scoped hard delete with cascade-aware confirmation. Delegates to handleDeleteRegion.
  const handleToggleRegionActive = handleDeleteRegion;

  const handleToggleProgramActive = async (programId) => {
    let affectedCount = 0;
    try {
      const usage = await axios.get(`${API}/crm/programs/${programId}/usage?user_id=${user.user_id}`);
      affectedCount = usage.data?.customers_count || 0;
    } catch (e) { /* non-blocking */ }

    setConfirmDialog({
      open: true,
      title: '⚠ Οριστική Διαγραφή Προγράμματος',
      message: affectedCount > 0
        ? `Αυτό το πρόγραμμα υπάρχει στα ενδιαφέροντα ${affectedCount} πελάτη/ων. Αν το διαγράψετε, θα αφαιρεθεί από όλους. Δεν αναιρείται.`
        : 'Θα διαγραφεί οριστικά. Δεν αναιρείται.',
      affectedCount,
      onConfirm: async () => {
        try {
          const resp = await axios.delete(`${API}/crm/programs/${programId}?user_id=${user.user_id}`);
          const affected = resp.data?.affected_customers || 0;
          if (affected > 0) toast.warning(`Το πρόγραμμα διαγράφηκε. Αφαιρέθηκε από ${affected} πελάτες.`);
          else toast.success('Το πρόγραμμα διαγράφηκε');
          fetchData();
        } catch (error) {
          const status = error?.response?.status;
          if (status === 404) toast.error('Το πρόγραμμα δεν βρέθηκε ή δεν σας ανήκει');
          else toast.error('Σφάλμα διαγραφής');
        }
      },
    });
  };

  const handleToggleCategoryActive = async (categoryId) => {
    let affectedCount = 0;
    try {
      const usage = await axios.get(`${API}/crm/categories/${categoryId}/usage?user_id=${user.user_id}`);
      affectedCount = usage.data?.customers_count || 0;
    } catch (e) { /* non-blocking */ }

    setConfirmDialog({
      open: true,
      title: '⚠ Οριστική Διαγραφή Κατηγορίας',
      message: affectedCount > 0
        ? `Αυτή η κατηγορία χρησιμοποιείται από ${affectedCount} πελάτη/ες. Αν την διαγράψετε, θα μείνουν χωρίς κατηγορία (κενό πεδίο). Δεν αναιρείται.`
        : 'Θα διαγραφεί οριστικά. Δεν αναιρείται.',
      affectedCount,
      onConfirm: async () => {
        try {
          const resp = await axios.delete(`${API}/crm/categories/${categoryId}?user_id=${user.user_id}`);
          const affected = resp.data?.affected_customers || 0;
          if (affected > 0) toast.warning(`Η κατηγορία διαγράφηκε. ${affected} πελάτες έμειναν χωρίς κατηγορία.`);
          else toast.success('Η κατηγορία διαγράφηκε');
          fetchData();
        } catch (error) {
          const status = error?.response?.status;
          if (status === 404) toast.error('Η κατηγορία δεν βρέθηκε ή δεν σας ανήκει');
          else toast.error('Σφάλμα διαγραφής');
        }
      },
    });
  };


  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/crm')}
              data-testid="back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Πίσω
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Ρυθμίσεις CRM</h1>
              <p className="text-sm text-slate-600">Διαχείριση περιφερειών, προγραμμάτων και κατηγοριών</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs defaultValue="regions">
          <TabsList className="mb-4">
            <TabsTrigger value="regions" data-testid="tab-regions">
              <MapPin className="h-4 w-4 mr-2" />
              Περιφέρειες ({regions.filter(r => r.is_active).length})
            </TabsTrigger>
            <TabsTrigger value="programs" data-testid="tab-programs">
              <FileText className="h-4 w-4 mr-2" />
              Προγράμματα ({programs.filter(p => p.is_active).length})
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">
              <Building2 className="h-4 w-4 mr-2" />
              Κατηγορίες ({categories.filter(c => c.is_active).length})
            </TabsTrigger>
          </TabsList>

          {/* Regions Tab */}
          <TabsContent value="regions">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold text-slate-700">Περιφέρειες</h3>
                <Button size="sm" onClick={() => openRegionDialog()} data-testid="add-region-button">
                  <Plus className="h-4 w-4 mr-1" />
                  Νέα Περιφέρεια
                </Button>
              </div>
              
              {loading ? (
                <div className="p-8 text-center text-slate-500">Φόρτωση...</div>
              ) : regions.length === 0 ? (
                <div className="p-8 text-center text-slate-500">Δεν υπάρχουν περιφέρειες</div>
              ) : (
                <div className="divide-y">
                  {regions.map(region => (
                    <div 
                      key={region.region_id} 
                      className={`p-4 flex items-center justify-between ${!region.is_active ? 'bg-slate-50 opacity-60' : ''}`}
                      data-testid={`region-row-${region.region_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        <div>
                          <div className="font-medium text-slate-900">{region.name}</div>
                          {region.code && (
                            <div className="text-xs text-slate-500">Κωδικός: {region.code}</div>
                          )}
                          {!region.is_active && (
                            <span className="text-xs text-red-600">Ανενεργή</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openRegionDialog(region)}
                          data-testid={`edit-region-${region.region_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleToggleRegionActive(region.region_id)}
                          data-testid={`delete-region-${region.region_id}`}
                          title="Διαγραφή"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Programs Tab */}
          <TabsContent value="programs">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold text-slate-700">Προγράμματα Ενδιαφέροντος</h3>
                <Button size="sm" onClick={() => openProgramDialog()} data-testid="add-program-button">
                  <Plus className="h-4 w-4 mr-1" />
                  Νέο Πρόγραμμα
                </Button>
              </div>
              
              {loading ? (
                <div className="p-8 text-center text-slate-500">Φόρτωση...</div>
              ) : programs.length === 0 ? (
                <div className="p-8 text-center text-slate-500">Δεν υπάρχουν προγράμματα</div>
              ) : (
                <div className="divide-y">
                  {programs.map(program => (
                    <div 
                      key={program.program_id} 
                      className={`p-4 flex items-center justify-between ${!program.is_active ? 'bg-slate-50 opacity-60' : ''}`}
                      data-testid={`program-row-${program.program_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-purple-500" />
                        <div>
                          <div className="font-medium text-slate-900">{program.name}</div>
                          {program.description && (
                            <div className="text-sm text-slate-500">{program.description}</div>
                          )}
                          {!program.is_active && (
                            <span className="text-xs text-red-600">Ανενεργό</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openProgramDialog(program)}
                          data-testid={`edit-program-${program.program_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleToggleProgramActive(program.program_id)}
                          data-testid={`delete-program-${program.program_id}`}
                          title="Διαγραφή"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold text-slate-700">Κατηγορίες Επιχειρήσεων</h3>
                <Button size="sm" onClick={() => openCategoryDialog()} data-testid="add-category-button">
                  <Plus className="h-4 w-4 mr-1" />
                  Νέα Κατηγορία
                </Button>
              </div>
              
              {loading ? (
                <div className="p-8 text-center text-slate-500">Φόρτωση...</div>
              ) : categories.length === 0 ? (
                <div className="p-8 text-center text-slate-500">Δεν υπάρχουν κατηγορίες</div>
              ) : (
                <div className="divide-y">
                  {categories.map(category => (
                    <div 
                      key={category.category_id} 
                      className={`p-4 flex items-center justify-between ${!category.is_active ? 'bg-slate-50 opacity-60' : ''}`}
                      data-testid={`category-row-${category.category_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Building2 className="h-4 w-4 text-amber-500" />
                        <div>
                          <div className="font-medium text-slate-900">{category.name}</div>
                          {!category.is_active && (
                            <span className="text-xs text-red-600">Ανενεργή</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openCategoryDialog(category)}
                          data-testid={`edit-category-${category.category_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleToggleCategoryActive(category.category_id)}
                          data-testid={`delete-category-${category.category_id}`}
                          title="Διαγραφή"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Region Dialog */}
      <Dialog open={regionDialogOpen} onOpenChange={setRegionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingRegion ? 'Επεξεργασία Περιφέρειας' : 'Νέα Περιφέρεια'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveRegion} className="space-y-4">
            <div>
              <Label>Όνομα *</Label>
              <Input
                value={regionForm.name}
                onChange={(e) => setRegionForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="π.χ. Αττική"
                required
                data-testid="region-name-input"
              />
            </div>
            <div>
              <Label>Κωδικός (προαιρετικό)</Label>
              <Input
                value={regionForm.code}
                onChange={(e) => setRegionForm(prev => ({ ...prev, code: e.target.value }))}
                placeholder="π.χ. ATT"
                data-testid="region-code-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setRegionDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button type="submit" data-testid="save-region-button">
                Αποθήκευση
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Program Dialog */}
      <Dialog open={programDialogOpen} onOpenChange={setProgramDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingProgram ? 'Επεξεργασία Προγράμματος' : 'Νέο Πρόγραμμα'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveProgram} className="space-y-4">
            <div>
              <Label>Όνομα *</Label>
              <Input
                value={programForm.name}
                onChange={(e) => setProgramForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="π.χ. Ψηφιακός Μετασχηματισμός"
                required
                data-testid="program-name-input"
              />
            </div>
            <div>
              <Label>Περιγραφή (προαιρετικό)</Label>
              <Input
                value={programForm.description}
                onChange={(e) => setProgramForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Σύντομη περιγραφή"
                data-testid="program-description-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setProgramDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button type="submit" data-testid="save-program-button">
                Αποθήκευση
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Επεξεργασία Κατηγορίας' : 'Νέα Κατηγορία'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveCategory} className="space-y-4">
            <div>
              <Label>Όνομα *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="π.χ. Λιανικό εμπόριο"
                required
                data-testid="category-name-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button type="submit" data-testid="save-category-button">
                Αποθήκευση
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Unified Delete Confirmation AlertDialog (mobile-friendly) */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, open }))}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-base whitespace-pre-line">
              {confirmDialog.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-btn">Άκυρο</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                const fn = confirmDialog.onConfirm;
                setConfirmDialog(prev => ({ ...prev, open: false }));
                if (fn) await fn();
              }}
            >
              Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CRMSettings;
